import type { RefObject } from 'react';
import { useMemo, useState } from 'react';

import { useTracking } from '@change-corgi/core/react/tracking';

import { useDecisionMakerProfilePageData } from 'src/app/pages/decisionMakerProfile/pageContext';

type Result = ModelHookResult<
	{
		displayName: string;
		avatarUrl: string;
		responseText: string;
		showReadMoreLink: boolean;
	},
	{
		onClickReadMoreLink: (responseTextRef: RefObject<HTMLDivElement | null>) => void;
		onClickPetitionLink: () => void;
	}
>;

export function useResponseListCard(responseId: string, petitionId: string, message: string): Result {
	const maxResponseSize = 250;
	const {
		data: { displayName, photo },
	} = useDecisionMakerProfilePageData();
	const [readMoreClicked, setReadMoreClicked] = useState(false);
	const [showReadMoreLink, setShowReadMoreLink] = useState(message.length > maxResponseSize);

	const track = useTracking();

	const onClickReadMoreLink = (responseTextRef: RefObject<HTMLDivElement | null>): void => {
		void track('dm_profile_redesign_response_click', { response_id: responseId });
		setReadMoreClicked(!readMoreClicked);
		setShowReadMoreLink(!showReadMoreLink);
		if (responseTextRef.current) {
			responseTextRef.current.focus();
		}
	};

	const onClickPetitionLink = () => {
		void track('dm_profile_redesign_response_petition_click', {
			petition_id: petitionId,
			response_id: responseId,
		});
	};

	const responseText = useMemo(() => {
		if (readMoreClicked || message.length <= maxResponseSize) return message;
		return `${message.slice(0, maxResponseSize)}...`;
	}, [message, readMoreClicked]);

	return {
		data: {
			displayName,
			avatarUrl: photo?.userSmall.url || 'https://static.change.org/profile-img/default-user-profile.svg',
			responseText,
			showReadMoreLink,
		},
		actions: {
			onClickReadMoreLink,
			onClickPetitionLink,
		},
	};
}
